import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "dna-helix";

const DEFAULT_BG = "#0a0f1f";
const DEFAULT_STRAND_A = "#22d3ee";
const DEFAULT_STRAND_B = "#a78bfa";
const DEFAULT_RUNG = "#fef3c7";

const HELIX_RADIUS_FRAC = 0.16;   // helix radius as a fraction of canvas width
const TURNS_PER_HEIGHT = 1.6;     // how many full twists fit in the canvas height
const SAMPLES_PER_TURN = 36;      // points sampled per full helical turn (smoothness)
const ROTATION_SPEED = 0.012;     // radians per frame around the vertical axis
const RUNG_EVERY = 6;             // draw a base-pair rung every Nth sample
const PERSPECTIVE = 0.55;         // 0..1 — how strongly z-depth scales node size/alpha

function rand(min, max) { return min + Math.random() * (max - min); }

export default function DnaHelix({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_STRAND_A, DEFAULT_STRAND_B, DEFAULT_RUNG]);
  const angleRef = useRef(0);
  const twinklesRef = useRef([]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_STRAND_A),
      pickColor(colors, 2, DEFAULT_STRAND_B),
      pickColor(colors, 3, DEFAULT_RUNG),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedTwinkles = () => {
      // Faint background "specks" — sells the molecular / microscope vibe
      // without competing with the helix.
      const count = Math.floor((width * height) / 12000);
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = {
          x: rand(0, width),
          y: rand(0, height),
          r: rand(0.4, 1.2),
          phase: rand(0, Math.PI * 2),
          period: rand(0.4, 1.4),
        };
      }
      twinklesRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedTwinkles();
    };

    const tick = (ts) => {
      const [bg, strandA, strandB, rung] = colorsRef.current;

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Background twinkles.
      const t = ts / 1000;
      const tw = twinklesRef.current;
      for (let i = 0; i < tw.length; i += 1) {
        const s = tw[i];
        const a = 0.12 + 0.20 * (0.5 + 0.5 * Math.sin(t * s.period + s.phase));
        ctx.fillStyle = rgba(strandA, a);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Helix geometry. Points are parameterised by `i` (vertical sample
      // index). Strand A's angle = baseTwist + globalRotation; strand B is
      // exactly π out of phase. The 3D point is then projected: x and y
      // map to canvas coords; z drives perspective (size + brightness).
      angleRef.current += ROTATION_SPEED;

      const cx = width / 2;
      const radius = width * HELIX_RADIUS_FRAC;
      const totalSamples = Math.max(8, Math.floor(SAMPLES_PER_TURN * TURNS_PER_HEIGHT * (height / 400)));
      const yStep = height / Math.max(1, totalSamples - 1);
      const angleStep = (Math.PI * 2 * TURNS_PER_HEIGHT) / Math.max(1, totalSamples - 1);

      // Pre-compute every sample for both strands so we can sort rungs +
      // nodes by depth and draw them back-to-front.
      const nodes = [];
      const rungs = [];
      for (let i = 0; i < totalSamples; i += 1) {
        const y = i * yStep;
        const baseAngle = i * angleStep + angleRef.current;
        // Strand A.
        const ax = cx + Math.cos(baseAngle) * radius;
        const az = Math.sin(baseAngle); // -1..1 — used for depth shading
        // Strand B (offset by π → on the other side of the helix).
        const bx = cx + Math.cos(baseAngle + Math.PI) * radius;
        const bz = Math.sin(baseAngle + Math.PI);

        nodes.push({ x: ax, y, z: az, slot: 1 });
        nodes.push({ x: bx, y, z: bz, slot: 2 });

        // Rungs: connect strand A and strand B every Nth sample.
        if (i % RUNG_EVERY === 0) {
          // Mean z of the rung determines its draw order vs nodes.
          rungs.push({
            x1: ax, y1: y, z1: az,
            x2: bx, y2: y, z2: bz,
            zMean: (az + bz) * 0.5,
          });
        }
      }

      // Build draw order: every node and every rung as separate items,
      // sorted back-to-front by their z (smaller z = farther = drawn first).
      const items = new Array(nodes.length + rungs.length);
      let p = 0;
      for (let i = 0; i < nodes.length; i += 1) items[p++] = { kind: "node", z: nodes[i].z, ref: nodes[i] };
      for (let i = 0; i < rungs.length; i += 1) items[p++] = { kind: "rung", z: rungs[i].zMean, ref: rungs[i] };
      items.sort((a, b) => a.z - b.z);

      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        if (it.kind === "node") {
          const n = it.ref;
          // depth in [0..1]: 0 = far (small/dim), 1 = near (large/bright).
          const depth = (n.z + 1) * 0.5;
          const sizeScale = 1 - PERSPECTIVE + PERSPECTIVE * depth;
          const radiusN = 3.6 * sizeScale;
          const alpha = 0.35 + 0.65 * depth;
          const color = n.slot === 1 ? strandA : strandB;

          // Soft halo + crisp core for a luminous nucleotide look.
          const haloR = radiusN * 4;
          const halo = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, haloR);
          halo.addColorStop(0, rgba(color, 0.45 * alpha));
          halo.addColorStop(1, rgba(color, 0));
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(n.x, n.y, haloR, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = rgba(color, alpha);
          ctx.beginPath();
          ctx.arc(n.x, n.y, radiusN, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const r = it.ref;
          const depth = (r.zMean + 1) * 0.5;
          const alpha = 0.20 + 0.55 * depth;
          ctx.strokeStyle = rgba(rung, alpha);
          ctx.lineWidth = 1 + depth * 1.2;
          ctx.beginPath();
          ctx.moveTo(r.x1, r.y1);
          ctx.lineTo(r.x2, r.y2);
          ctx.stroke();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
